import { Component, effect, forwardRef, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, NgControl, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UI_FORM_IMPORTS } from '@program-suite/ui';
import { BaseControlValueAccessor } from 'libs/shared/ui-common/src/lib/components/form/base-control-value-accessor';
import { LookupViewModel } from '@program-suite/data-common';

export interface AgencyFormModel {
  id?: number;
  agencyName: string;
  licenseNumber?: string | null;
  streetAddress: string;
  suite?: string | null;
  city: string;
  state?: string | null;
  zipCode: string;
  phone: string;
  websiteUrl?: string | null;
  programIds?: number[] | null;
  status?: string | null;
}

@Component({
  selector: 'lib-ui-agency-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ...UI_FORM_IMPORTS,
    MatCheckboxModule,
  ],
  templateUrl: './ui-agency-form.component.html',
  styleUrl: './ui-agency-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiAgencyFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UiAgencyFormComponent),
      multi: true,
    },
  ],
})
export class UiAgencyFormComponent extends BaseControlValueAccessor<AgencyFormModel>  {
  formGroup = input<FormGroup | null>(null);
  programs = input<LookupViewModel[]>([]);
  states = input<LookupViewModel[]>([]);
  selectedProgramIds: number[] = [];
  agencyForm: FormGroup;
  
  public override onChange: (value: any) => void = () => {};

  constructor(
    private fb: FormBuilder,
  ) {
    super();

    // For form validation see this.validate() method
    this.agencyForm = this.fb.group({
      id: [null],
      agencyName: ['', [Validators.maxLength(100), Validators.required]],
      licenseNumber: ['', [Validators.maxLength(20), Validators.required]],
      streetAddress: ['', [Validators.maxLength(100), Validators.required]],
      suite: ['', Validators.maxLength(50)],
      city: ['', [Validators.maxLength(50), Validators.required]],
      state: ['', [Validators.required, Validators.required]],
      zipCode: ['', [Validators.maxLength(10), Validators.required]],
      agencyEmail: ['', [Validators.maxLength(100)]],
      phone: ['', [Validators.maxLength(20), Validators.required]],
      websiteUrl: ['', [Validators.maxLength(150), Validators.required]],
      programIds: [[], [Validators.required]],
      status: ['Active'],
      checkboxes: new FormArray([]),
    });

    effect(() => {
      const programs = this.programs();
      if (programs && programs.length > 0) {
        this.addCheckboxes();
      }
    });

    this.agencyForm.valueChanges.subscribe(() => {
      this.updateProgramIds();
      // Notify parent form
      this.onChange(this.agencyForm.value);
    });
  }

  get checkboxesFormArray() {
    return this.agencyForm.controls['checkboxes'] as FormArray;
  }

  override registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  // Gives the parent form control access to the validation errors
  // If you add validation to the form, you need to add that field here
  validate(control: AbstractControl) {
    if (this.agencyForm.valid) {
      return null;
    }
    let errors : any = {};
    errors = this.addControlErrors(errors, "agencyName");
    errors = this.addControlErrors(errors, "licenseNumber");
    errors = this.addControlErrors(errors, "streetAddress");
    errors = this.addControlErrors(errors, "city");
    errors = this.addControlErrors(errors, "state");
    errors = this.addControlErrors(errors, "zipCode");
    errors = this.addControlErrors(errors, "phone");
    errors = this.addControlErrors(errors, "programIds");
    return errors;
  }
  
  addControlErrors(allErrors: any, controlName:string) {
    const errors = {...allErrors};
    const controlErrors = this.agencyForm.controls[controlName].errors;
    if (controlErrors) {
      errors[controlName] = controlErrors;
    }
    return errors;
  }

  private addCheckboxes() {
    this.checkboxesFormArray.clear();
    const selectedIds = this.agencyForm.get('programIds')?.value || [];
    this.programs().forEach((program) => {
      const programId = +(program?.id ?? 0);
      if (programId > 0) {
        const isChecked = selectedIds.includes(programId);
        this.checkboxesFormArray.push(this.fb.control(isChecked));
      }
    });
  }

  private updateProgramIds() {
    if (this.checkboxesFormArray.length !== this.programs().length) {
      return;
    }
    const selectedProgramIds = this.programs()
      .map((program, index) => (this.checkboxesFormArray.at(index).value ? +(program.id ?? 0) : null))
      .filter((id) => id !== null);
    this.agencyForm.get('programIds')?.setValue(selectedProgramIds, { emitEvent: false });
  }

  override writeValue(value: any | null): void { 
    if (value) {
      this.agencyForm.patchValue(value || {}, { emitEvent: false });

      if (!value.status) {
        value.status = 'Active';
      }

      if (value.stateId) {
        const matchingState = this.states().find(state => state.id === value.stateId.toString());
        if (matchingState) {
          this.agencyForm.get('state')?.setValue(matchingState.id, { emitEvent: false });
        } else {
          console.warn('State not found in states() options');
        }
      }
      
      if (this.programs().length > 0) {
        this.addCheckboxes();
      }
    }
  }
}
